import { lightingOf, withLighting } from '../engine/battlefield/lighting';
import { useState, useCallback } from 'react';
import { clearCells, emptyGrid } from '../engine/battlefield/voxels';
import { brushTipCells, volumeKeys } from '../engine/battlefield/buildGesture';
import { strokeLevel, strokeTerrain, clearedTerrain } from '../engine/battlefield/terrainEdits';
import { useBuildSettings, useGestureOptions, useStructureOptions } from './useBuildOptions';
import { useMaterialPalette, useSurfacePalette, useAssetImport } from './useBuildPalette';
import { useArenaDraw } from './useArenaDraw';
import { useBuildClipboard } from './useBuildClipboard';
import { useStructureEdits } from './useStructureEdits';
import { useBuildStroke, useCellUnder } from './useBuildStroke';

export const BUILD_TOOLS = [
  // `icon` is a key into MAP_ICONS, not a glyph - see components/MapIcon.
  { key: 'fill', label: 'Fill', icon: 'fill', hint: 'Stack cubes' },
  { key: 'erase', label: 'Erase', icon: 'erase', hint: 'Take cubes away' },
  { key: 'zone', label: 'Zone', icon: 'zone', hint: 'Paint ground: hazards, rough going' },
  { key: 'dig', label: 'Dig', icon: 'dig', hint: 'Cut a trench below the ground' },
  { key: 'select', label: 'Select', icon: 'select', hint: 'Copy a region, mirror it, stamp it again' },
  // The shapes cubes do badly - and the only things on the map that
  // nothing could reach until v8.1.0.
  {
    key: 'structure',
    label: 'Shape',
    icon: 'ngon',
    hint: 'Pillars, cones and domes - click to place, click one to select',
  },
];

/**
 * useBattlefieldBuild - laying a battlefield out with cubes.
 *
 * Four tools and two gestures, and the gestures do not care which tool
 * is active: RECT builds the barracks, BRUSH carves the hillside, and
 * `buildGesture` answers "which cells?" for both. Everything here is
 * plumbing between the pointer and those pure functions - and, since the
 * quality paydown, composition: the settings, palette, clipboard,
 * structures, arena draft and stroke each live in a hook of their own,
 * and what a stroke does to the terrain is `engine/battlefield/terrainEdits`.
 *
 * Right-drag always erases, whichever tool is up - the one shortcut
 * worth having, because taking a cube back is the commonest correction
 * anyone makes.
 */
export default function useBattlefieldBuild({ getCamera, terrain, setTerrain }) {
  const settings = useBuildSettings();
  const gestures = useGestureOptions();
  const [hover, setHover] = useState(null);
  const tile = terrain.boundary.tileSize > 0 ? terrain.boundary.tileSize : 1;
  const grid = terrain.voxels || emptyGrid(tile);
  const edit = { terrain, setTerrain, grid, tile };
  const { selectStroke, ...tools } = useBuildTools(edit, settings.tool, hover);
  const { arenaPointerDown, ...arena } = useArenaDraw({ getCamera, terrain, setTerrain });
  const rules = useStrokeRules({ ...edit, settings, gestures, tools, selectStroke });
  const cellUnder = useCellUnder(getCamera, terrain.boundary, tile);
  const { preview, start, move, finish, wheel, cancel } = useBuildStroke({ cellUnder, ...rules, setHover, settings });
  // While an arena is being drawn every click feeds the ring instead.
  const onPointerDown = useCallback(
    (e) => (arena.arenaDraft ? arenaPointerDown(e) : start(e)),
    [arena.arenaDraft, arenaPointerDown, start],
  );
  return {
    ...settings,
    ...gestures,
    ...tools,
    ...arena,
    hover,
    setHover,
    preview,
    cancelStroke: cancel,
    buildHandlers: { onPointerDown, onPointerMove: move, onPointerUp: finish, onPointerCancel: finish },
    onBuildWheel: wheel,
  };
}

/** Everything the build panel edits besides the stroke: palette, clipboard, structures. */
function useBuildTools(edit, tool, hover) {
  const structureOptions = useStructureOptions();
  const materials = useMaterialPalette(edit, tool);
  const surfaces = useSurfacePalette(edit);
  const importAssets = useAssetImport({ ...edit, surfaces: surfaces.surfaces, materials: materials.materials });
  const clipboard = useBuildClipboard({ ...edit, tool, hover });
  const structures = useStructureEdits({ ...edit, spec: structureSpec(structureOptions, materials) });
  const resets = useTerrainResets({ ...edit, setSelected: structures.setSelected });
  const lighting = useSceneLighting(edit);
  return {
    ...structureOptions,
    ...materials,
    ...surfaces,
    ...clipboard,
    ...structures,
    ...resets,
    ...lighting,
    importAssets,
  };
}

/** How the scene is lit, and the pick that changes it (engine/battlefield/lighting). */
function useSceneLighting({ terrain, setTerrain }) {
  const setLighting = useCallback((next) => setTerrain(withLighting(terrain, next)), [terrain, setTerrain]);
  return { lighting: lightingOf(terrain), setLighting };
}

/** The Shape tool's next structure, without a place. */
function structureSpec(options, palette) {
  return {
    shape: options.structureShape,
    radius: options.structureRadius,
    height: options.structureHeight,
    rotation: options.structureRotation,
    material: palette.materials[palette.material],
  };
}

/** Take one cube away, or everything. */
function useTerrainResets({ terrain, setTerrain, grid, tile, setSelected }) {
  const removeAt = useCallback(
    (cell, atLevel) => {
      setTerrain({ ...terrain, voxels: clearCells(grid, volumeKeys([cell], atLevel, 1)) });
    },
    [terrain, setTerrain, grid],
  );
  const clearTerrain = useCallback(() => {
    setSelected(null);
    setTerrain(clearedTerrain(terrain, tile));
  }, [terrain, setTerrain, tile, setSelected]);
  return { removeAt, clearTerrain };
}

/** A new drag at `cell`: the gesture options and level, frozen for its length. */
function newDrag(e, cell, options, levelFor) {
  // Right-drag always takes away, whichever tool is up.
  const erasing = e.button === 2 || options.tool === 'erase';
  const { gesture, brushSize, shape, sides, hollow, tip, thickness, layers } = options;
  const painted = gesture === 'brush' ? brushTipCells(cell, brushSize, tip) : [];
  const level = levelFor(cell, erasing);
  return {
    id: e.pointerId,
    gesture,
    from: cell,
    to: cell,
    painted,
    brushSize,
    shape,
    sides,
    hollow,
    tip,
    thickness,
    erasing,
    level,
    layers,
  };
}

/**
 * How a stroke begins (`begin`) and what it builds when it ends
 * (`commit`). Select only marks or stamps; every other tool is a terrain
 * edit worked out by `strokeTerrain`.
 */
function useStrokeRules({ terrain, setTerrain, grid, settings, gestures, tools, selectStroke }) {
  const { tool, level, layers, autoStack, zoneLabel } = settings;
  const { material, zonePreset } = tools;
  const levelFor = useCallback(
    (cell, erasing) => strokeLevel({ tool, level, autoStack, grid }, cell, erasing),
    [tool, level, autoStack, grid],
  );
  const begin = useCallback(
    (e, cell) => newDrag(e, cell, { ...gestures, tool, layers }, levelFor),
    [gestures, tool, layers, levelFor],
  );
  const commit = useCallback(
    (d, footprint) => {
      if (tool === 'select') {
        selectStroke(d);
        return;
      }
      const eraseLevel = (cell) => levelFor(cell, true);
      const ctx = { terrain, grid, material, eraseLevel, layers, zonePreset, zoneLabel };
      const next = strokeTerrain(tool, ctx, { footprint, erasing: d.erasing, level: d.level, layers: d.layers });
      if (next) setTerrain(next);
    },
    [tool, terrain, grid, material, levelFor, layers, zonePreset, zoneLabel, setTerrain, selectStroke],
  );
  return { begin, commit };
}
